import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { csvCell } from '../../utils/csv';
import { DateRangePresetFilter } from '../shared/DateRangePresetFilter';
import { getFileTimestamp, formatDT } from '../../utils/timezone';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { Checkbox } from '../ui/checkbox';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../ui/popover';
import { Calendar } from '../ui/calendar';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import {
  Shield, RefreshCw, Search, Copy, Trash2, Eye, ExternalLink, Loader2,
  Clock, Film, Clapperboard, ArrowUpDown, ArrowUp, ArrowDown,
  Mail, MessageSquare, Users, Upload, Download, Activity,
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, X, MoreVertical,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useIsMobileOrWebview } from '../../hooks/useIsMobileOrWebview';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { useMedia } from '../../contexts/MediaContext';
import { SyncedTableScroll } from '../admin/SyncedTableScroll';
import { ShareLinkActivityDrawer } from './ShareLinkActivityDrawer';
import { PaginationFooter } from '../shared/PaginationFooter';
import { useUrlPage, useUrlPageSize, useDebouncedValue, useSkipFirstEffect } from '../shared/useUrlPagination';

const FIXED_ACTIONS_WIDTH = 'w-[80px]'; // single "⋮" overflow menu
const ROWS_PER_PAGE_DEFAULT = 50;

const statusOfLink = (link) => {
  if (link.revoked_at) return 'revoked';
  try {
    const exp = new Date(link.expires_at);
    if (!isNaN(exp.getTime()) && exp.getTime() < Date.now()) return 'expired';
  } catch { /* ignore */ }
  return 'active';
};

/**
 * SharedEvidence — cross-source share-link audit table.
 *
 * Backend: GET /api/media/shared-evidence/links
 * Persistence: see MediaContext (seSearchQuery / seSourceFilter / …).
 *
 * Each row tracks:
 *   - Creator (name + email)
 *   - Recipients (Email/SMS sends with timestamp + sender)
 *   - Clipboard copies (timestamp + operator email — recorded via
 *     /share-links/{id}/record-copy on every successful copy click)
 *   - View count (single increment per public-page load; never double)
 *
 * CSV export: respects current filters; downloads as
 *   shared-evidence-<YYYY-MM-DD>.csv with columns
 *   Type, Device, Clip, Created, Created-by, Expires, Views, Sends, Copies, Status.
 */
export const SharedEvidence = () => {
  const { api } = useAuth();
  const isMobileOrWebview = useIsMobileOrWebview();
  const menuItemCls = isMobileOrWebview ? 'min-h-[52px] text-sm px-3 py-3' : '';
  const menuIconCls = isMobileOrWebview ? '!w-5 !h-5 mr-3' : 'w-4 h-4 mr-2';
  const {
    seSearchQuery, setSeSearchQuery,
    seSourceFilter, setSeSourceFilter,
    seStatusFilter, setSeStatusFilter,
    seSortField, setSeSortField,
    seSortDir, setSeSortDir,
    seDateFromIso, setSeDateFromIso,
    seDateToIso, setSeDateToIso,
  } = useMedia();

  // Lift the persisted ISO strings up to live Date objects for the Calendar.
  const dateRange = useMemo(() => ({
    from: seDateFromIso ? new Date(seDateFromIso) : undefined,
    to: seDateToIso ? new Date(seDateToIso) : undefined,
  }), [seDateFromIso, seDateToIso]);

  const setDateRange = useCallback((r) => {
    setSeDateFromIso(r?.from ? r.from.toISOString() : null);
    setSeDateToIso(r?.to ? r.to.toISOString() : null);
  }, [setSeDateFromIso, setSeDateToIso]);

  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  // Sticks to true after first fetch — prevents the loader block from
  // remounting the table on subsequent search refetches.
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [error, setError] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revoking, setRevoking] = useState(false);
  const [recipientsTarget, setRecipientsTarget] = useState(null);
  const [activityTarget, setActivityTarget] = useState(null);
  const [page, setPage] = useUrlPage('page');  // 1-indexed; resets when filters change
  const [rowsPerPage, setRowsPerPage] = useUrlPageSize('shared-evidence', 10);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ active: 0, revoked: 0, expired: 0 });
  const [selectAllLoading, setSelectAllLoading] = useState(false);
  // Row selection — mirrors the Audit Log pattern: per-row checkbox + header
  // select-all, plus a "N selected · Select All · Clear" bar that appears
  // above the table. Export honours the selection when non-empty, else
  // exports every currently-visible row.
  const [selectedIds, setSelectedIds] = useState([]);
  const [dateResetKey, setDateResetKey] = useState(0);

  const debouncedSearch = useDebouncedValue(seSearchQuery || '', 400);

  // Server-side filter params shared by list fetch, Select All and CSV export.
  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim());
    if (seSourceFilter !== 'all') params.set('source', seSourceFilter);
    if (seStatusFilter !== 'all') params.set('status', seStatusFilter);
    if (seDateFromIso) params.set('from_date', seDateFromIso);
    if (seDateToIso) params.set('to_date', seDateToIso);
    return params;
  }, [debouncedSearch, seSourceFilter, seStatusFilter, seDateFromIso, seDateToIso]);

  const fetchLinks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = buildFilterParams();
      if (seSortField) { params.set('sort_field', seSortField); params.set('sort_dir', seSortDir || 'desc'); }
      params.set('skip', String((page - 1) * rowsPerPage));
      params.set('limit', String(rowsPerPage));
      const res = await api.get(`/media/shared-evidence/links?${params.toString()}`);
      setLinks(Array.isArray(res.data?.links) ? res.data.links : []);
      setTotal(res.data?.total || 0);
      setCounts(res.data?.counts || { active: 0, revoked: 0, expired: 0 });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to load share links';
      setError(detail);
      if (err?.response?.status !== 403) toast.error(detail);
    } finally {
      setLoading(false);
      setInitialLoadDone(true);
    }
  }, [api, buildFilterParams, seSortField, seSortDir, page, rowsPerPage]);

  useEffect(() => { fetchLinks(); }, [fetchLinks]);

  const handleRefresh = useCallback(() => {
    // Refresh also snaps the date filter back to All Time (Event Log behavior).
    setDateRange({ from: undefined, to: undefined });
    setDateResetKey(k => k + 1);
    fetchLinks();
  }, [setDateRange, fetchLinks]);

  // Rows come pre-filtered / sorted / paged from the server.
  const visibleLinks = links;

  // Reset to page 1 whenever a filter changes — otherwise the user can end
  // up "stuck" on an empty page. Skips mount so ?page= deep-links survive.
  useSkipFirstEffect(() => { setPage(1); }, [debouncedSearch, seSourceFilter, seStatusFilter, seSortField, seSortDir, seDateFromIso, seDateToIso, rowsPerPage]);

  const pageRows = links;

  const toggleSort = (field) => {
    if (seSortField === field) setSeSortDir(seSortDir === 'asc' ? 'desc' : 'asc');
    else { setSeSortField(field); setSeSortDir('desc'); }
  };

  const SortIcon = ({ field }) => {
    if (seSortField !== field) return <ArrowUpDown className="w-3 h-3 inline-block ml-1 opacity-50" />;
    return seSortDir === 'asc'
      ? <ArrowUp className="w-3 h-3 inline-block ml-1" />
      : <ArrowDown className="w-3 h-3 inline-block ml-1" />;
  };

  // Issue #2: copy click also records an audit event server-side so admins
  // can see "who else has the URL" beyond Email/SMS sends.
  const copyToClipboard = async (link) => {
    try {
      await navigator.clipboard.writeText(link.url);
      toast.success('Share link copied to clipboard');
    } catch {
      toast.error('Failed to copy — your browser may have blocked clipboard access');
      return;
    }
    // Fire-and-forget audit ping. Refresh the row locally so badges update.
    try {
      await api.post(`/media/event-videos/share-links/${link.id}/record-copy`);
      setLinks(prev => prev.map(l => l.id === link.id ? {
        ...l,
        copy_count: (l.copy_count || 0) + 1,
        last_copied_at: new Date().toISOString(),
        delivery_channels: Array.from(new Set([...(l.delivery_channels || []), 'copy'])),
      } : l));
    } catch (err) {
      // Audit failure is non-blocking — clipboard already has the URL.

      console.warn('Copy-audit failed', err);
    }
  };

  const confirmRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      await api.delete(`/media/event-videos/share-links/${revokeTarget.id}`);
      toast.success('Share link revoked');
      setRevokeTarget(null);
      fetchLinks();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to revoke share link');
    } finally {
      setRevoking(false);
    }
  };

  const totalsForBadges = useMemo(() => ({
    total: (counts.active || 0) + (counts.revoked || 0) + (counts.expired || 0),
    active: counts.active || 0,
    revoked: counts.revoked || 0,
    expired: counts.expired || 0,
  }), [counts]);

  // Issue #4 — CSV export of the currently-visible rows. When the user
  // has ticked one or more row checkboxes the export narrows to only
  // those rows; otherwise every row that matches the current filter set
  // is exported (matches the Audit Log "Export All / Export (N)" UX).
  const exportCsv = useCallback(async () => {
    let rowsToExport = [];
    try {
      // Fetch ALL rows matching the current filters (no skip/limit = full set).
      const params = buildFilterParams();
      if (seSortField) { params.set('sort_field', seSortField); params.set('sort_dir', seSortDir || 'desc'); }
      const res = await api.get(`/media/shared-evidence/links?${params.toString()}`);
      const all = Array.isArray(res.data?.links) ? res.data.links : [];
      rowsToExport = selectedIds.length > 0 ? all.filter(l => selectedIds.includes(l.id)) : all;
    } catch (err) {
      toast.error('Failed to fetch rows for export');
      return;
    }
    if (!rowsToExport.length) {
      toast.info('No rows to export');
      return;
    }
    const headers = [
      'Type', 'Device', 'Clip', 'Created', 'Created by',
      'Expires', 'Views', 'Downloads', 'Download enabled', 'Sends', 'Copies', 'Last sent to', 'Status',
    ];
    const rows = rowsToExport.map(l => [
      l.source === 'remote_download' ? 'Download' : 'Event',
      l.device_label || '',
      l.clip_label || '',
      l.created_at ? formatDT(l.created_at, { seconds: true }) : '',
      l.created_by_email || '',
      l.expires_at ? formatDT(l.expires_at, { seconds: true }) : '',
      l.view_count || 0,
      l.download_count || 0,
      l.allow_download ? 'Yes' : 'No',
      l.send_count || 0,
      l.copy_count || 0,
      l.last_sent_to || '',
      statusOfLink(l),
    ]);
    const csv = [headers, ...rows].map((r, ri) => r.map((v, ci) => {
      // 'Last sent to' (col 11) is a mobile number — emit as Excel text so
      // leading zeros aren't stripped / no scientific notation.
      if (ri > 0 && ci === 11) {
        const s = String(v ?? '').trim();
        if (s) return `="${s.replace(/"/g, '""')}"`;
      }
      return csvCell(v);
    }).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const today = getFileTimestamp();
    a.href = url;
    a.download = `shared-evidence-${today}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rowsToExport.length} row${rowsToExport.length === 1 ? '' : 's'}`);
  }, [api, buildFilterParams, seSortField, seSortDir, selectedIds]);

  return (
    <div className="space-y-4" data-testid="shared-evidence-page">
      {/* Header — matches the Live Videos / Event Videos / Remote Media pattern:
          icon + title + description on the left. Export/Refresh live on the
          filters row so they sit next to each other. */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-foreground" />
            <h2 className="text-xl font-semibold" data-testid="shared-evidence-title">Shared Evidence</h2>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">Manage every clip you have shared externally — revoke, resend, or export the history.</p>
        </div>
      </div>

      {/* Filters row — search + source/status/date/export/refresh */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={seSearchQuery}
            onChange={(e) => setSeSearchQuery(e.target.value)}
            placeholder="Search device, note, creator, recipient…"
            className="h-8 pl-8 pr-8"
            data-testid="shared-evidence-search"
          />
          {seSearchQuery && (
            <Button type="button" variant="ghost" size="sm" className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0" onClick={() => setSeSearchQuery('')} data-testid="shared-evidence-clear-search">
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
        <Select value={seSourceFilter} onValueChange={setSeSourceFilter}>
          <SelectTrigger className="h-8 w-[180px]" data-testid="shared-evidence-source-filter">
            <SelectValue placeholder="All sources" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            <SelectItem value="event_video">Event Videos</SelectItem>
            <SelectItem value="remote_download">Remote Downloads</SelectItem>
          </SelectContent>
        </Select>
        <Select value={seStatusFilter} onValueChange={setSeStatusFilter}>
          <SelectTrigger className="h-8 w-[150px]" data-testid="shared-evidence-status-filter">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="revoked">Revoked</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
          </SelectContent>
        </Select>
        {/* Date range — Event Log "All Time" preset filter (shared component). */}
        <DateRangePresetFilter value={dateRange} onChange={setDateRange} testIdPrefix="shared-evidence-date" resetSignal={dateResetKey} />
        <Button size="sm" variant="outline" onClick={exportCsv} disabled={selectedIds.length === 0} data-testid="shared-evidence-export">
          <Upload className="w-4 h-4 mr-2" />
          {selectedIds.length > 0 ? `Export (${selectedIds.length})` : 'Export'}
        </Button>
        <Button size="sm" variant="outline" onClick={handleRefresh} disabled={loading} data-testid="shared-evidence-refresh">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Refresh
        </Button>
      </div>

      {/* Count badges row — under the search bar per UI consistency request. */}
      <div className="flex flex-wrap items-center gap-2" data-testid="shared-evidence-counts-row">
        <Badge variant="outline" data-testid="shared-evidence-total">{totalsForBadges.total} Total</Badge>
        <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="shared-evidence-active">{counts.active} Active</Badge>
        {counts.revoked > 0 && (
          <Badge variant="secondary" className="bg-slate-100 text-slate-600" data-testid="shared-evidence-revoked">{counts.revoked} Revoked</Badge>
        )}
        {counts.expired > 0 && (
          <Badge variant="secondary" className="bg-amber-50 text-amber-700 border-amber-200" data-testid="shared-evidence-expired">{counts.expired} Expired</Badge>
        )}
      </div>

      {/* Selection bar — mirrors Audit Log: appears only when at least one
          row is ticked, offers Select-All / Deselect-All toggle scoped to
          the currently-visible (filtered) rows, plus a quick Clear. */}
      <div className="flex items-center gap-3 text-sm text-muted-foreground px-1 h-6 -my-1.5 overflow-hidden" data-testid="shared-evidence-selection-bar">
          <span data-testid="shared-evidence-selection-count">{selectedIds.length} selected</span>
{" "}
                  {selectedIds.length > 0 && (<>
          <Button variant="ghost" size="sm" className="h-6 px-2 text-xs"
            data-testid="shared-evidence-select-all-toggle"
            disabled={selectAllLoading}
            onClick={async () => {
              if (selectedIds.length >= total) { setSelectedIds([]); return; }
              setSelectAllLoading(true);
              try {
                const params = buildFilterParams();
                params.set('ids_only', 'true');
                const res = await api.get(`/media/shared-evidence/links?${params.toString()}`);
                setSelectedIds(Array.isArray(res.data?.ids) ? res.data.ids : []);
              } catch (err) {
                toast.error('Failed to select all rows');
              } finally {
                setSelectAllLoading(false);
              }
            }}
          >
            {selectAllLoading ? 'Selecting…' : (selectedIds.length >= total ? 'Deselect All' : 'Select All')}
          </Button>
          <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setSelectedIds([])} data-testid="shared-evidence-selection-clear">Clear</Button>
        </>)}
                  </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {!initialLoadDone ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading shared evidence…
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground" data-testid="shared-evidence-error">
              <Shield className="w-10 h-10 mb-2 opacity-60" />
              <p className="text-sm">{error}</p>
            </div>
          ) : visibleLinks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground" data-testid="shared-evidence-empty">
              <Shield className="w-12 h-12 mb-3 opacity-50" />
              <p className="text-base font-medium">No share links</p>
              <p className="text-xs mt-1">{total === 0 && !debouncedSearch && seStatusFilter === 'all' && seSourceFilter === 'all' && !seDateFromIso ? "You haven't created any share links yet." : "No links match the current filters."}</p>
            </div>
          ) : (
            <SyncedTableScroll minWidth="1660px" testId="shared-evidence-table-scroll" className={loading ? 'opacity-60 pointer-events-none transition-opacity' : 'transition-opacity'}>
              <Table data-testid="shared-evidence-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        data-testid="shared-evidence-select-all"
                        checked={pageRows.length > 0 && pageRows.every(l => selectedIds.includes(l.id))}
                        onCheckedChange={(checked) => {
                          const pageIds = pageRows.map(l => l.id).filter(Boolean);
                          setSelectedIds(prev => checked
                            ? Array.from(new Set([...prev, ...pageIds]))
                            : prev.filter(id => !pageIds.includes(id))
                          );
                        }}
                      />
                    </TableHead>
                    <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('source')}>
                      Type <SortIcon field="source" />
                    </TableHead>
                    <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('device_label')}>
                      Device <SortIcon field="device_label" />
                    </TableHead>
                    <TableHead>Clip</TableHead>
                    <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('created_by_email')}>
                      Created by <SortIcon field="created_by_email" />
                    </TableHead>
                    <TableHead>Recipients</TableHead>
                    <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('created_at')}>
                      Created <SortIcon field="created_at" />
                    </TableHead>
                    <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('expires_at')}>
                      Expires <SortIcon field="expires_at" />
                    </TableHead>
                    <TableHead className="cursor-pointer select-none text-center" onClick={() => toggleSort('view_count')}>
                      Views <SortIcon field="view_count" />
                    </TableHead>
                    <TableHead className="cursor-pointer select-none text-center" onClick={() => toggleSort('download_count')}>
                      Downloads <SortIcon field="download_count" />
                    </TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className={`text-center ${FIXED_ACTIONS_WIDTH}`}>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pageRows.map((link) => {
                    const status = statusOfLink(link);
                    const isRevokable = status === 'active';
                    const sourceLabel = link.source === 'remote_download' ? 'Download' : 'Event';
                    const SourceIcon = link.source === 'remote_download' ? Clapperboard : Film;
                    const copies = link.copy_count || 0;
                    const recentAudit = link.recent_recipients || [];
                    // Surface the actual recipient address (most recent per channel)
                    // so the column is informative at a glance — Issue #2 follow-up.
                    const lastEmail = recentAudit.find(r => r.method === 'email');
                    const lastSms = recentAudit.find(r => r.method === 'sms');
                    const emailTotal = recentAudit.filter(r => r.method === 'email').length;
                    const smsTotal = recentAudit.filter(r => r.method === 'sms').length;
                    const hasAnyDelivery = lastEmail || lastSms || copies > 0;
                    const recipientCell = (
                      <div className="flex items-center gap-2 text-xs min-w-0 max-w-[260px]">
                        {lastEmail && (
                          <span className="inline-flex items-center gap-1 min-w-0">
                            <Mail className="w-3 h-3 text-blue-600 shrink-0" />
                            <span className="truncate" title={lastEmail.recipient || ''}>{lastEmail.recipient || '—'}</span>
                            {emailTotal > 1 && <span className="text-muted-foreground shrink-0">+{emailTotal - 1}</span>}
                          </span>
                        )}
                        {lastSms && (
                          <span className="inline-flex items-center gap-1 min-w-0">
                            <MessageSquare className="w-3 h-3 text-emerald-600 shrink-0" />
                            <span className="truncate" title={lastSms.recipient || ''}>{lastSms.recipient || '—'}</span>
                            {smsTotal > 1 && <span className="text-muted-foreground shrink-0">+{smsTotal - 1}</span>}
                          </span>
                        )}
                        {copies > 0 && (
                          <span className="inline-flex items-center gap-1 text-amber-700">
                            <Copy className="w-3 h-3 text-amber-600 shrink-0" />
                            <span>{copies}×</span>
                          </span>
                        )}
                        {!hasAnyDelivery && <span className="text-muted-foreground">—</span>}
                      </div>
                    );
                    return (
                      <TableRow key={link.id} data-testid={`shared-evidence-row-${link.id}`} className={selectedIds.includes(link.id) ? 'bg-blue-50 dark:bg-blue-950/20' : ''}>
                        <TableCell onClick={e => e.stopPropagation()}>
                          <Checkbox
                            data-testid={`shared-evidence-row-checkbox-${link.id}`}
                            checked={selectedIds.includes(link.id)}
                            onCheckedChange={(checked) => setSelectedIds(prev =>
                              checked ? [...prev, link.id] : prev.filter(id => id !== link.id)
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5 text-xs" data-testid={`shared-evidence-source-${link.id}`}>
                            <SourceIcon className="w-3.5 h-3.5 text-[#2563eb]" />
                            {sourceLabel}
                          </div>
                        </TableCell>
                        <TableCell className="font-medium text-sm" data-testid={`shared-evidence-device-${link.id}`}>
                          {link.device_label || '—'}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {link.clip_label || '—'}
                          {!link.clip_exists && (
                            <Badge variant="outline" className="ml-2 text-amber-700 border-amber-200 bg-amber-50 text-[10px]">Deleted</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-xs" data-testid={`shared-evidence-creator-${link.id}`}>
                          {link.created_by_email ? (
                            <span title={link.created_by_name || link.created_by_email}>{link.created_by_email}</span>
                          ) : (
                            <span className="text-muted-foreground italic">unknown</span>
                          )}
                        </TableCell>
                        <TableCell data-testid={`shared-evidence-recipients-${link.id}`}>
                          {hasAnyDelivery ? (
                            <button
                              type="button"
                              onClick={() => setRecipientsTarget(link)}
                              className="text-left hover:underline focus:outline-none w-full"
                              title="View full delivery history"
                              data-testid={`shared-evidence-recipients-open-${link.id}`}
                            >
                              {recipientCell}
                            </button>
                          ) : (
                            recipientCell
                          )}
                        </TableCell>
                        <TableCell className="text-xs whitespace-nowrap">
                          {link.created_at ? formatDistanceToNow(new Date(link.created_at), { addSuffix: true }) : '—'}
                        </TableCell>
                        <TableCell className="text-xs whitespace-nowrap">
                          {link.expires_at ? (
                            <span className="inline-flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDistanceToNow(new Date(link.expires_at), { addSuffix: true })}
                            </span>
                          ) : '—'}
                        </TableCell>
                        <TableCell className="text-center" data-testid={`shared-evidence-views-${link.id}`}>
                          {link.view_count || 0}
                        </TableCell>
                        <TableCell className="text-center" data-testid={`shared-evidence-downloads-${link.id}`}>
                          {link.allow_download ? (
                            <span className="inline-flex items-center gap-1 justify-center text-emerald-700">
                              <Download className="w-3 h-3" /> {link.download_count || 0}
                            </span>
                          ) : (
                            <span className="text-muted-foreground" title="Download not enabled for this link">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {status === 'active' && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Active</Badge>}
                          {status === 'revoked' && <Badge variant="secondary" className="bg-slate-100 text-slate-600">Revoked</Badge>}
                          {status === 'expired' && <Badge className="bg-amber-100 text-amber-700 border-amber-200">Expired</Badge>}
                        </TableCell>
                        <TableCell className={`text-center ${FIXED_ACTIONS_WIDTH}`}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="outline" className={isMobileOrWebview ? 'h-9 w-9 p-0' : ''} title="Actions" data-testid={`shared-evidence-actions-menu-${link.id}`}>
                                <MoreVertical className={isMobileOrWebview ? '!w-5 !h-5' : 'w-4 h-4'} />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className={isMobileOrWebview ? 'w-64 p-1.5' : 'w-56'}>
                              <DropdownMenuItem className={menuItemCls} disabled={!link.url} onSelect={() => copyToClipboard(link)} data-testid={`shared-evidence-copy-${link.id}`}>
                                <Copy className={menuIconCls} /> Copy public URL
                              </DropdownMenuItem>
                              <DropdownMenuItem className={menuItemCls} disabled={!link.url} onSelect={() => { if (link.url) window.open(link.url, '_blank', 'noopener,noreferrer'); }} data-testid={`shared-evidence-open-${link.id}`}>
                                <ExternalLink className={menuIconCls} /> Open in new tab
                              </DropdownMenuItem>
                              <DropdownMenuItem className={menuItemCls} onSelect={() => setActivityTarget(link)} data-testid={`shared-evidence-activity-${link.id}`}>
                                <Activity className={menuIconCls} /> Activity log
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {isRevokable ? (
                                <DropdownMenuItem className={`${menuItemCls} text-rose-600 focus:text-rose-600 dark:text-rose-400 dark:focus:text-rose-400`} onSelect={() => setRevokeTarget(link)} data-testid={`shared-evidence-revoke-${link.id}`}>
                                  <Trash2 className={menuIconCls} /> Revoke link
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem className={menuItemCls} disabled data-testid={`shared-evidence-revoke-disabled-${link.id}`}>
                                  <Trash2 className={menuIconCls} /> {status === 'revoked' ? 'Already revoked' : 'Expired'}
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </SyncedTableScroll>
          )}
          {/* Pagination footer — always visible (even during load / empty)
              so table cards keep a consistent height across states. */}
          {!error && (
            <PaginationFooter
              page={page}
              setPage={setPage}
              totalItems={total}
              itemsPerPage={rowsPerPage}
              setItemsPerPage={setRowsPerPage}
              testIdPrefix="shared-evidence"
              disabled={loading}
              alwaysShow
            />
          )}
        </CardContent>
      </Card>

      {/* Recipients drill-down */}
      <Dialog open={!!recipientsTarget} onOpenChange={(o) => { if (!o) setRecipientsTarget(null); }}>
        <DialogContent className="max-w-xl" data-testid="shared-evidence-recipients-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Users className="w-4 h-4" /> Delivery history</DialogTitle>
            <DialogDescription>
              {recipientsTarget && (
                <span className="block">
                  {recipientsTarget.device_label} • {recipientsTarget.clip_label}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          {recipientsTarget && (
            <div className="space-y-3 text-sm">
              <div className="rounded-md bg-muted px-3 py-2 text-xs">
                <span className="font-medium">Created by:</span> {recipientsTarget.created_by_email || 'unknown'}
              </div>
              {(recipientsTarget.recent_recipients || []).length === 0 ? (
                <p className="text-muted-foreground text-xs italic">No deliveries yet. Use the copy / send buttons to share this link.</p>
              ) : (
                <div className="space-y-2">
                  {(recipientsTarget.recent_recipients || []).map((r, idx) => (
                    <div key={`${r.at}-${idx}`} className="flex items-start gap-2 rounded-md border px-3 py-2 text-xs">
                      <div className="mt-0.5">
                        {r.method === 'email' && <Mail className="w-3.5 h-3.5 text-blue-600" />}
                        {r.method === 'sms' && <MessageSquare className="w-3.5 h-3.5 text-emerald-600" />}
                        {r.method === 'copy' && <Copy className="w-3.5 h-3.5 text-amber-600" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">
                          {r.method === 'copy'
                            ? `Copied to clipboard`
                            : `Sent ${r.method === 'sms' ? 'SMS' : 'email'} to ${r.recipient || '—'}`}
                        </div>
                        <div className="text-muted-foreground flex items-center gap-2 mt-0.5 flex-wrap">
                          <span>by {r.sent_by_email || 'unknown'}</span>
                          {r.at && (
                            <>
                              <span>·</span>
                              <span title={r.at}>{format(new Date(r.at), 'PPp')}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Per-link activity drawer — views + downloads with timestamp + IP */}
      <ShareLinkActivityDrawer
        open={!!activityTarget}
        onOpenChange={(o) => { if (!o) setActivityTarget(null); }}
        shareId={activityTarget?.id}
        label={activityTarget ? `${activityTarget.device_label || 'Unknown device'} • ${activityTarget.clip_label || ''}` : null}
      />

      {/* Revoke confirm */}
      <AlertDialog open={!!revokeTarget} onOpenChange={(o) => { if (!o) setRevokeTarget(null); }}>
        <AlertDialogContent data-testid="shared-evidence-revoke-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke share link?</AlertDialogTitle>
            <AlertDialogDescription>
              Recipients of this link will no longer be able to view the clip. This action cannot be undone — you'll need to create a new share link if you change your mind.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {revokeTarget && (
            <div className="rounded-md bg-muted px-3 py-2 text-sm">
              <div className="font-medium">{revokeTarget.device_label}</div>
              <div className="text-xs text-muted-foreground">{revokeTarget.clip_label}</div>
              {revokeTarget.note && <div className="text-xs text-muted-foreground italic mt-1">"{revokeTarget.note}"</div>}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoking}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmRevoke(); }}
              disabled={revoking}
              className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-600"
              data-testid="shared-evidence-revoke-confirm"
            >
              {revoking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Revoke link
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SharedEvidence;

